import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface CreditTransaction {
  id: string;
  user_id: string;
  order_id: string | null;
  amount_aed: number;
  transaction_type: 'credit' | 'redemption';
  description: string | null;
  source: string | null;
  admin_note: string | null;
  created_at: string;
}

interface CustomerCredits {
  balance_aed: number;
  transactions: CreditTransaction[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useCustomerCredits = (): CustomerCredits => {
  const { user } = useAuth();
  const [balance, setBalance] = useState<number>(0);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCredits = useCallback(async () => {
    if (!user) {
      setBalance(0);
      setTransactions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch balance
      const { data: creditData, error: creditError } = await supabase
        .from('customer_credits')
        .select('balance_aed')
        .eq('user_id', user.id)
        .maybeSingle();

      if (creditError) {
        throw creditError;
      }

      setBalance(creditData?.balance_aed || 0);

      // Fetch transactions - select all columns including source and admin_note
      const { data: txData, error: txError } = await supabase
        .from('credit_transactions')
        .select('id, user_id, order_id, amount_aed, transaction_type, description, source, admin_note, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (txError) {
        throw txError;
      }

      // Cast the transaction_type to our expected type
      setTransactions((txData || []).map(tx => ({
        id: tx.id,
        user_id: tx.user_id,
        order_id: tx.order_id,
        amount_aed: tx.amount_aed,
        transaction_type: tx.transaction_type as 'credit' | 'redemption',
        description: tx.description,
        source: (tx as any).source || null,
        admin_note: (tx as any).admin_note || null,
        created_at: tx.created_at,
      })));
    } catch (err) {
      console.error('Error fetching customer credits:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch credits');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchCredits();
  }, [fetchCredits]);

  return {
    balance_aed: balance,
    transactions,
    loading,
    error,
    refetch: fetchCredits,
  };
};

/**
 * Calculate how many ticket credits can be applied to an order.
 * 
 * Rules:
 * - Each accessory purchase = 1 "ticket credit" worth the net amount paid
 * - Max 1 ticket can be redeemed per cart item
 * - Tickets are applied FIFO (oldest first)
 * - Accessories are not eligible for credit redemption
 * - Each ticket applies its FULL value (variable per ticket)
 * 
 * @param ticketTransactions - Individual credit transactions (tickets) sorted oldest first
 * @param cartItems - Cart items with price and accessory flag
 * @returns Total redemption amount and per-item breakdown
 */
export const calculateCreditRedemptionWithTickets = (
  ticketTransactions: { amount_aed: number; id: string }[],
  cartItems: { price: number; isAccessory?: boolean; quantity?: number }[],
): { totalRedemption: number; itemRedemptions: { index: number; amount: number; ticketId: string }[]; ticketsUsed: number } => {
  if (!ticketTransactions.length) {
    return { totalRedemption: 0, itemRedemptions: [], ticketsUsed: 0 };
  }

  // Filter out accessory items - credits don't apply to accessories
  const eligibleItems = cartItems
    .map((item, index) => ({ ...item, index }))
    .filter(item => !item.isAccessory);

  // Apply 1 ticket per unit (quantity), FIFO
  const itemRedemptions: { index: number; amount: number; ticketId: string }[] = [];
  let ticketsUsed = 0;
  let totalRedemption = 0;

  for (const item of eligibleItems) {
    const qty = item.quantity || 1;
    let itemTotalRedemption = 0;

    // Apply one ticket per unit in this line item
    for (let u = 0; u < qty; u++) {
      // Stop if we've used all available tickets
      if (ticketsUsed >= ticketTransactions.length) break;

      // Apply the next available ticket (FIFO - oldest first)
      const ticket = ticketTransactions[ticketsUsed];
      itemTotalRedemption += ticket.amount_aed;
      totalRedemption += ticket.amount_aed;
      ticketsUsed++;
    }

    if (itemTotalRedemption > 0) {
      itemRedemptions.push({
        index: item.index,
        amount: itemTotalRedemption,
        ticketId: ticketTransactions[ticketsUsed - 1].id,
      });
    }
  }

  return { totalRedemption, itemRedemptions, ticketsUsed };
};

/**
 * Legacy function for backwards compatibility - uses balance-based calculation
 * @deprecated Use calculateCreditRedemptionWithTickets for accurate FIFO ticket application
 */
export const calculateCreditRedemption = (
  availableCredits: number,
  cartItems: { price: number; isAccessory?: boolean }[],
): { totalRedemption: number; itemRedemptions: { index: number; amount: number }[]; ticketsUsed: number } => {
  // For legacy compatibility, estimate tickets from balance
  // This won't be accurate for variable-value tickets but provides a fallback
  const estimatedTicketCount = availableCredits > 0 ? 1 : 0; // Assume at least 1 ticket if balance > 0
  
  if (estimatedTicketCount <= 0) {
    return { totalRedemption: 0, itemRedemptions: [], ticketsUsed: 0 };
  }

  const eligibleItems = cartItems
    .map((item, index) => ({ ...item, index }))
    .filter(item => !item.isAccessory);

  const itemRedemptions: { index: number; amount: number }[] = [];
  let ticketsUsed = 0;
  let totalRedemption = 0;
  let remainingCredits = availableCredits;

  for (const item of eligibleItems) {
    if (remainingCredits <= 0) break;

    // Apply credits up to the available amount for this item
    const redemptionAmount = Math.min(remainingCredits, item.price);
    
    itemRedemptions.push({
      index: item.index,
      amount: redemptionAmount,
    });
    
    totalRedemption += redemptionAmount;
    remainingCredits -= redemptionAmount;
    ticketsUsed++;
  }

  return { totalRedemption, itemRedemptions, ticketsUsed };
};
